import React from 'react';
import { Box, Typography, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import BuildIcon from '@material-ui/icons/Build';
import { motion } from 'framer-motion';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #2196f3 0%, #673ab7 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.05,
    background: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
  },
  contentBox: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    padding: theme.spacing(4),
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2196f3 0%, #673ab7 100%)',
    marginBottom: theme.spacing(3),
    boxShadow: '0 10px 30px rgba(33, 150, 243, 0.4)',
  },
  icon: {
    fontSize: '60px',
    color: '#ffffff',
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #2196f3 0%, #673ab7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.7,
  },
  infoBox: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    background: 'rgba(33, 150, 243, 0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(33, 150, 243, 0.2)',
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  pulseAnimation: {
    animation: '$pulse 2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1.05)',
      opacity: 0.8,
    },
  },
}));

const MaintenancePage = () => {
  const classes = useStyles();

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
  };

  return (
    <Box className={classes.root}>
      <div className={classes.backgroundPattern} />
      <Container maxWidth="md">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Box className={classes.contentBox}>
            <motion.div variants={iconVariants}>
              <Box className={classes.iconWrapper}>
                <BuildIcon className={classes.icon} />
              </Box>
            </motion.div>

            <Typography variant="h2" className={classes.title}>
              We&apos;ll Be Right Back
            </Typography>

            <Typography variant="h6" className={classes.subtitle}>
              We&apos;re currently performing scheduled maintenance to improve your experience.
              Our team is working hard to get everything back up and running as soon as possible.
            </Typography>

            <Box className={classes.infoBox}>
              <Typography className={classes.infoText}>
                <strong>What&apos;s happening?</strong>
                <br />
                We&apos;re upgrading our systems to serve you better. This maintenance is essential
                to ensure optimal performance and security.
              </Typography>
            </Box>

            <Box className={classes.infoBox} sx={{ marginTop: 2 }}>
              <Typography className={classes.infoText}>
                <strong>Need immediate assistance?</strong>
                <br />
                Please contact our support team at
                {' '}
                <a
                  href="mailto:help@preceptorlink.com"
                  style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}
                >
                  help@preceptorlink.com
                </a>
              </Typography>
            </Box>

            <Box mt={4}>
              <Typography variant="body2" style={{ color: '#999', fontSize: '0.75rem' }}>
                Thank you for your patience and understanding
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MaintenancePage;
